import CardType from "enums/CardType";
import DisputeStatus from "enums/DisputeStatus";
import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import { PERSONAL_EXPENSE_MEMO } from "components/common/Constants";

type LinkedTransactions = ({ type?: string | null } | null)[] | null;

// Mirrors disputeBlocksPersonalExpense in shared Constants. LOST returned nothing, so repaying is the only recovery left.
const disputeBlocksPersonalExpense = (disputeStatus?: string | null): boolean =>
  Boolean(disputeStatus) && disputeStatus !== DisputeStatus.LOST;

// Mirrors hasLinkedMerchantRefund in the shared Transaction service: a refund is its own row and leaves the capture COMPLETE.
const hasLinkedMerchantRefund = (transaction?: {
  linkedTransactions?: LinkedTransactions;
}): boolean =>
  (transaction?.linkedTransactions ?? []).some(
    (tx) => tx?.type === TransactionsType.REFUND,
  );

/** Who is asking: the owner is the cardholder or payee, a manager is Manager and above. */
export interface ActionViewer {
  isOwner: boolean;
  isManager: boolean;
  personalExpenseEnabled: boolean;
}

export interface PersonalExpenseFields {
  status?: string | null;
  type?: string | null;
  amount?: number | null;
  memo?: string | null;
  // Required so a list cannot ask a rule without selecting what blocks it.
  disputeStatus: string | null | undefined;
  linkedTransactions: LinkedTransactions | undefined;
}

export interface DisputeFields extends PersonalExpenseFields {
  disputeId?: string | null;
  card?: { type?: string | null } | null;
  user?: { id?: string | null } | null;
}

// A purchase that can carry a personal expense: not the repayment transfer itself, and not refunded.
const isPersonalExpenseCandidate = (
  transaction: PersonalExpenseFields,
): boolean =>
  (transaction.amount ?? 0) < 0 &&
  transaction.type === TransactionsType.CAPTURE &&
  !(transaction.memo ?? "")
    .toLowerCase()
    .includes(PERSONAL_EXPENSE_MEMO.toLowerCase()) &&
  !hasLinkedMerchantRefund(transaction);

const canSeePersonalExpenseActions = (
  transaction: PersonalExpenseFields,
  viewer: ActionViewer,
): boolean =>
  viewer.personalExpenseEnabled &&
  (viewer.isOwner || viewer.isManager) &&
  isPersonalExpenseCandidate(transaction);

const canMarkPersonalExpense = (
  transaction: PersonalExpenseFields,
  viewer: ActionViewer,
): boolean =>
  canSeePersonalExpenseActions(transaction, viewer) &&
  transaction.status === TransactionStatus.COMPLETE &&
  !disputeBlocksPersonalExpense(transaction.disputeStatus);

// Mirrors cancelPersonalExpense; unlike marking and repaying, a dispute does not block it (#1415).
const canRevertPersonalExpense = (
  transaction: PersonalExpenseFields,
  viewer: ActionViewer,
): boolean =>
  viewer.personalExpenseEnabled &&
  (viewer.isOwner || viewer.isManager) &&
  transaction.status === TransactionStatus.PERSONAL_PENDING;

// Mirrors payPersonalExpense, which only the owner may call, and processPersonalExpenseRepayment.
const canRepayPersonalExpense = (
  transaction: PersonalExpenseFields,
  viewer: Pick<ActionViewer, "isOwner" | "personalExpenseEnabled">,
): boolean =>
  viewer.personalExpenseEnabled &&
  viewer.isOwner &&
  (transaction.status === TransactionStatus.PERSONAL_PENDING ||
    transaction.status === TransactionStatus.PERSONAL_FAILED) &&
  !disputeBlocksPersonalExpense(transaction.disputeStatus) &&
  !hasLinkedMerchantRefund(transaction);

const NON_DISPUTABLE_STATUSES: (string | null | undefined)[] = [
  TransactionStatus.DECLINED,
  TransactionStatus.REVERSED,
  TransactionStatus.CANCELED,
  TransactionStatus.FAILED,
  TransactionStatus.INSUFFICIENT_FUNDS,
];

const canDisputeTransaction = (transaction: DisputeFields): boolean =>
  (!transaction.disputeStatus ||
    (Boolean(transaction.disputeId) &&
      ["CREATED", "INFORMATION_REQUIRED"].includes(
        transaction.disputeStatus,
      ))) &&
  (transaction.type === TransactionsType.AUTHORIZATION ||
    transaction.type === TransactionsType.CAPTURE) &&
  transaction.card?.type !== CardType.EXTERNAL &&
  !transaction.user?.id?.includes("OUTPAVE_EXTERNAL") &&
  !NON_DISPUTABLE_STATUSES.includes(transaction.status) &&
  // A prefix test, as PERSONAL_EXPENSE_STATUSES is on the server, which createTransactionDispute enforces
  !(transaction.status?.startsWith("PERSONAL") ?? false) &&
  !hasLinkedMerchantRefund(transaction);

const canAddPayeeBank = (
  transaction: { status?: string | null },
  viewer: Pick<ActionViewer, "isOwner">,
): boolean =>
  viewer.isOwner &&
  transaction.status === TransactionStatus.PENDING_BANK_ACCOUNT;

// The invite is sendPersonalBankAccountEmail, so it follows that mutation's permission (useCanManageUsers).
const canInvitePayeeBank = (
  transaction: { status?: string | null },
  viewer: { isOwner: boolean; canManageUsers: boolean },
): boolean =>
  !viewer.isOwner &&
  viewer.canManageUsers &&
  transaction.status === TransactionStatus.PENDING_BANK_ACCOUNT;

export {
  disputeBlocksPersonalExpense,
  hasLinkedMerchantRefund,
  canSeePersonalExpenseActions,
  canMarkPersonalExpense,
  canRevertPersonalExpense,
  canRepayPersonalExpense,
  canDisputeTransaction,
  canAddPayeeBank,
  canInvitePayeeBank,
};

export default disputeBlocksPersonalExpense;
